import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Button, ButtonLink, Text } from '../common/components';
import { useDocumentTitle } from '../common/hooks';
import { t } from '../common/i18n';
import routes from '../common/constants/routes';
import {
  getUnsubscribeInfo,
  MonitoringNotificationType,
  setUnsubscribeState,
  UnsubscribeInfo,
  UnsubscribeLinkParams,
} from './services/NotificationUnsubscribeService';

const namespace = 'NotificationUnsubscribe';

type PageState = 'loading' | 'confirm' | 'unsubscribed' | 'resubscribed' | 'invalid';

const parseLinkParams = (searchParams: URLSearchParams): UnsubscribeLinkParams | null => {
  const buildingId = searchParams.get('buildingId');
  const profileId = searchParams.get('profileId');
  const type = searchParams.get('type');
  const signature = searchParams.get('signature');

  if (!buildingId || !profileId || !signature || (type !== 'change' && type !== 'report' && type !== 'deadline'))
    return null;

  return { buildingId, profileId, type: type as MonitoringNotificationType, signature };
};

/**
 * Veřejná stránka odhlášení e-mailů monitoringu z podepsaného odkazu v e-mailu (bez přihlášení).
 * Samotné odhlášení se potvrzuje tlačítkem (POST) – prefetch odkazu e-mailovým klientem nic nezmění.
 */
export const NotificationUnsubscribe: React.FC = () => {
  useDocumentTitle(t('title', namespace));

  const [searchParams] = useSearchParams();
  const linkParams = parseLinkParams(searchParams);

  const [state, setState] = useState<PageState>('loading');
  const [info, setInfo] = useState<UnsubscribeInfo | null>(null);
  const [isSubmitting, setSubmitting] = useState<boolean>(false);
  const [actionError, setActionError] = useState<boolean>(false);

  useEffect(() => {
    if (!linkParams) {
      setState('invalid');
      return;
    }

    getUnsubscribeInfo(linkParams)
      .then((result) => {
        setInfo(result);
        setState(result.enabled ? 'confirm' : 'unsubscribed');
      })
      .catch(() => setState('invalid'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetState = async (enabled: boolean) => {
    if (!linkParams) return;
    setSubmitting(true);
    setActionError(false);

    try {
      const result = await setUnsubscribeState(linkParams, enabled);
      setInfo(result);
      setState(result.enabled ? 'resubscribed' : 'unsubscribed');
    } catch {
      setActionError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const translationOptions = info
    ? { typeLabel: t(`type.${info.type}`, namespace), svjName: info.svjName }
    : undefined;

  return (
    <div className="OnboardingForm">
      <Text size="h1" color="Neutral800" className="mb-2">
        {t('heading', namespace)}
      </Text>

      {state === 'loading' && (
        <Text size="body-medium" color="Neutral600">
          {t('isLoading', namespace)}
        </Text>
      )}

      {state === 'invalid' && (
        <Text size="body-medium" color="Neutral600">
          {t('invalidLink.text', namespace)}
        </Text>
      )}

      {state === 'confirm' && info && (
        <>
          <Text size="body-medium" color="Neutral800" className="mb-1">
            {t('confirm.text', namespace, translationOptions)}
          </Text>
          <Text size="body-small" color="Neutral600" className="mb-3">
            {t('confirm.note', namespace)}
          </Text>
          <Button
            title={t('confirm.action', namespace)}
            loading={isSubmitting}
            onClick={() => handleSetState(false)}
          />
        </>
      )}

      {state === 'unsubscribed' && info && (
        <>
          <Text size="body-medium" color="Neutral800" className="mb-1">
            {t('unsubscribed.text', namespace, translationOptions)}
          </Text>
          <Text size="body-small" color="Neutral600" className="mb-3">
            {t('unsubscribed.note', namespace)}
          </Text>
          <Button
            variant="secondary"
            title={t('unsubscribed.action', namespace)}
            loading={isSubmitting}
            onClick={() => handleSetState(true)}
          />
        </>
      )}

      {state === 'resubscribed' && info && (
        <Text size="body-medium" color="Neutral800" className="mb-3">
          {t('resubscribed.text', namespace, translationOptions)}
        </Text>
      )}

      {actionError && (
        <Text size="body-small" color="SystemRed" className="mt-2">
          {t('error', namespace)}
        </Text>
      )}

      {state !== 'loading' && (
        <div className="mt-3">
          <ButtonLink variant="text" to={routes.login} title={t('toApp', namespace)} />
        </div>
      )}
    </div>
  );
};

export default NotificationUnsubscribe;
